import { getStack } from '@/lib/contentstack/stack';
import { getPriorityArticles } from './PriorityArticlesClient';
import { jsonToHtml } from '@contentstack/json-rte-serializer';
const locale = process.env.NEXT_PUBLIC_APP_LOCALE;

export async function getLatestArticlesClient(skipNum, limit) {
  skipNum = Number(skipNum);
  limit = Number(limit);

  try {
    const query = createArticleQuery(skipNum, limit);
    const articles = await query.find();
    if (!articles[0]) throw new Error('No articles found');

    return articles[0].map(formatArticle);
  } catch (error) {
    console.error('Error fetching latest articles:', error);
    throw error;
  }
}

function createArticleQuery(skipNum, limit) {
  const Stack = getStack();
  return Stack.ContentType('article')
    .Query()
    .addParam('include_dimension', 'true')
    .includeReference('category')
    .where('locale', locale)
    .query({ tags: { $nin: ['priority'] } })
    .skip(skipNum)
    .limit(limit)
    .descending('created_at')
    .toJSON();
}

function formatArticle(latest) {
  return {
    title: latest.title,
    url: latest.url,
    categoryConnection: {
      title: latest.category?.[0]?.title || '',
      url: latest.category?.[0]?.url || ''
    },
    date: latest.date,
    feature_image: latest.feature_image,
    json_rte: latest.json_rte
  };
}

export async function gatherData() {
  try {
    const latest = await getLatestArticlesClient(0, 6);
    const priorityArticles = await getPriorityArticles();

    const sliderArticles = mergeArticles(priorityArticles, latest);
    ensureMinimumArticles(sliderArticles, latest, 3);

    const descriptions = sliderArticles.map(generateDescription);

    return { sliderArticles, descriptions };
  } catch (error) {
    console.error('Error fetching articles:', error);
    throw error;
  }
}

function mergeArticles(priorityArticles, latest) {
  const sliderArticles = priorityArticles ? [...priorityArticles] : [];
  const latestWithoutDuplicates = latest.filter(
    (article) => !sliderArticles.some((item) => item.id === article.id)
  );
  sliderArticles.push(...latestWithoutDuplicates);
  return sliderArticles;
}

function ensureMinimumArticles(sliderArticles, latest, minCount) {
  if (sliderArticles.length < minCount) {
    const additionalArticlesNeeded = minCount - sliderArticles.length;
    sliderArticles.push(...latest.slice(0, additionalArticlesNeeded));
  }
}

function generateDescription(article) {
  let descriptionText = '';

  if (article.json_rte) {
    try {
      descriptionText = jsonToHtml(article.json_rte);
    } catch (error) {
      console.error('Error parsing JSON RTE:', error);
      descriptionText = '';
    }
  }

  const structuredContent = parseAndTruncateRichText(descriptionText);
  return structuredContentToReadableText(structuredContent, 220);
}

function structuredContentToReadableText(contentArray, maxChars = 220) {
  let accumulatedText = '';

  for (const item of contentArray) {
    if (item.type === 'heading' || item.type === 'paragraph') {
      const text = Array.isArray(item.content)
        ? item.content.join(' ')
        : item.content;
      accumulatedText += text + ' ';

      if (accumulatedText.length >= maxChars) {
        accumulatedText = accumulatedText.slice(0, maxChars) + '...';
        break;
      }
    }
  }

  return accumulatedText.trim();
}

function parseAndTruncateRichText(richText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(richText, 'text/html');
  const result = [];
  let paragraphGroup = [];

  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeName === 'P') {
      const paragraphText = node.textContent.trim();
      if (paragraphText) paragraphGroup.push(paragraphText);
    } else {
      if (paragraphGroup.length > 0) {
        result.push({
          type: 'paragraph',
          content: paragraphGroup
        });
        paragraphGroup = [];
      }

      if (node.nodeName === 'H2' || node.nodeName === 'H3') {
        result.push({
          type: 'heading',
          level: node.nodeName.toLowerCase(),
          content: node.textContent.trim()
        });
      }
    }
  });

  if (paragraphGroup.length > 0) {
    result.push({
      type: 'paragraph',
      content: paragraphGroup
    });
  }

  return result;
}
